import { COLOR, vendorRecommendations } from "@finway-group/shared/lib/consts"
import { RightEnum, UploadKindEnum, type Vendor } from "@finway-group/shared/lib/models"
import { MAX_INPUT_LENGTH } from "@finway-group/shared/lib/utils/validators"
import { Button, Col, Form, Input, Row, Select, Upload } from "antd"
import { type FormInstance } from "antd/lib/form"
import { type Store } from "antd/lib/form/interface"
import type { UploadRequestOption } from "rc-upload/lib/interface"
import React, { useEffect, useMemo, useState } from "react"
import { Upload as UploadIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import PhoneInput, { type Value } from "react-phone-number-input"
import "react-phone-number-input/style.css"

import { Link } from "react-router-dom"
import { HoverableInfo } from "~Components/forms/expenseForm/reimbursementForms/auxilliary/HoverableInfo"
import { createVendorRules } from "~Components/forms/rules/vendor.rules"
import { IbanInput } from "~Components/ibanInput"
import { WARNING_ORANGE } from "~Shared/config/consts"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/hooks/user.hooks"
import { AuthzService, FileService, VendorService } from "~Shared/services"
import { buildIsoCountrySelectOptions } from "~Shared/utils/country.utils"
import { doFilterSelect, getDisplayCountryCallingCode, getIBANPrintFormat, validateCreditorNumberDATEVCompatibility } from "~Shared/utils/helper.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"

const { Option } = Select

interface VendorFormProps {
    formInstance: FormInstance
    isNew: boolean
    vendor?: Vendor
    prefilledData?: Partial<Vendor>
    onSubmit: (values: Store) => void
    setIsUploading: (flag: boolean) => void
    onFormValuesChange: () => void
}
export const VendorForm = ({ formInstance, vendor, prefilledData, setIsUploading, isNew, onSubmit, onFormValuesChange }: VendorFormProps) => {
    const { t, i18n } = useTranslation()
    const countryCodeOptions = useMemo(() => buildIsoCountrySelectOptions(i18n.language), [i18n.language])
    const rules = createVendorRules()
    const companyDefaultCreditorNumber = useCompany()?.defaultCreditorNumber
    const [fileList, setFileList] = useState<Array<any>>([])
    const [contactPersonPhone, setContactPersonPhone] = useState(formInstance.getFieldValue("contactPersonPhone"))
    const [phoneInputPlaceholder, setPhoneInputPlaceholder] = useState(getDisplayCountryCallingCode())
    const [creditorNumberInfo, setCreditorNumberInfo] = useState<string | undefined>(undefined)
    const loggedInUser = useLoggedInEmployeeProfile()

    useEffect(() => {
        let isFetching = true

        const getLatestCreditorNumber = async () => {
            const latestSequence = await VendorService.getLatestVendorCreditorNumber()
            formInstance.setFieldsValue({ creditorNumber: latestSequence })
        }

        if (!isNew && vendor) {
            initFormWithVendorData()
        }
        if (isNew && isFetching) {
            getLatestCreditorNumber()
            if (prefilledData?.contactPersonPhone) {
                setContactPersonPhone(prefilledData.contactPersonPhone)
            }
        }

        return () => {
            isFetching = false
        }
    }, [vendor?.id])

    const initFormWithVendorData = () => {
        if (vendor!.iban) {
            vendor!.iban = getIBANPrintFormat(vendor!.iban)
        }
        if (vendor!.img) {
            const currentFileList = [
                {
                    uid: "1",
                    name: vendor!.name,
                    status: "done",
                    thumbUrl: vendor!.img,
                    type: "image/png",
                },
            ]
            setFileList(currentFileList)
        }
        setContactPersonPhone(vendor!.contactPersonPhone)
    }

    // TODO: Move this to consts as it is used on multiple places
    const doFileUpload = async (options: UploadRequestOption) => {
        const { onSuccess, onError, onProgress, file } = options
        if (!FileService.checkFileSizeAndHandleError(file as File)) {
            return
        }

        try {
            setIsUploading(true)
            const data = await FileService.upload(file as File, UploadKindEnum.VENDOR_IMG, onProgress)
            setIsUploading(false)
            onSuccess?.(data, data.url as unknown as XMLHttpRequest)
        } catch (err) {
            setIsUploading(false)
            onError?.(err)
        }
    }

    const handlePhoneNumberChange = (value: Value) => {
        formInstance.setFieldsValue({
            contactPersonPhone: value,
        })
        return {
            contactPersonPhone: value,
        }
    }

    const handleCountryChange = (value: any) => {
        if (value) {
            const displayCountryCallingCode = getDisplayCountryCallingCode(value)
            setPhoneInputPlaceholder(displayCountryCallingCode)
        }
    }

    const handleCreditorNumberChange = () => {
        const formValues = formInstance.getFieldsValue()
        const { creditorNumber } = formValues
        try {
            validateCreditorNumberDATEVCompatibility(creditorNumber)
            setCreditorNumberInfo(undefined)
        } catch (err) {
            setCreditorNumberInfo(t("error:vendor.incompatible_creditor_number"))
        }
    }

    const canEditVendor = isNew || AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR_PAYMENT_DATA__ALL__CREATE)

    return (
        <div>
            <Form layout="vertical" form={formInstance} onFinish={onSubmit} scrollToFirstError={true} onValuesChange={onFormValuesChange}>
                <Row gutter={[16, 16]} align="top">
                    <Col span={14} md="14">
                        <Form.Item
                            label={
                                <>
                                    {t("input:vendor.name")}
                                    <HoverableInfo message={t("tooltips:vendor.name_short")} type="info" />{" "}
                                </>
                            }
                            name="name"
                            rules={rules.name}
                            required
                        >
                            <Input placeholder={t("placeholder:vendor.company_name")} required autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={10} md="10">
                        <Form.Item
                            label={t("input:vendor.creditor_number")}
                            name="creditorNumber"
                            // Removed in: https://gitlab.com/finway-group/webapp/-/merge_requests/2616
                            // TODO GOB How can we fix this better? I'm sure we still need validation for this :(
                            //
                            // rules={rules.creditorNumber}
                            initialValue={companyDefaultCreditorNumber}
                            extra={
                                creditorNumberInfo && (
                                    <span className="-ml-4 ant-form-item-explain" style={{ color: WARNING_ORANGE }}>
                                        {creditorNumberInfo}
                                    </span>
                                )
                            }
                            rules={rules.creditorNumber}
                        >
                            <Input placeholder={t("placeholder:vendor.creditor_number")} onChange={handleCreditorNumberChange} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            label={
                                <>
                                    {t("input:vendor.nameExtended")}
                                    <HoverableInfo
                                        message={
                                            <span>
                                                {t("tooltips:vendor.name_extended_1")}
                                                <Link to={{ pathname: t("tooltips:vendor.name_extended_link_url") }} target="_blank">
                                                    {" "}
                                                    {t("tooltips:vendor.name_extended_link_label")}{" "}
                                                </Link>
                                                {t("tooltips:vendor.name_extended_2")}
                                            </span>
                                        }
                                        type="info"
                                    />
                                </>
                            }
                            name="nameExtended"
                            rules={rules.nameExtended}
                        >
                            <Input placeholder={t("placeholder:vendor.company_name")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("input:vendor.description")} name="description" rules={rules.description} normalize={(value) => (value === "" ? undefined : value)}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.description")} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("input:vendor.recommendation")} name="recommendation" initialValue={0} rules={rules.recommendation}>
                            <Select data-testid="finway:vendor:form:recommendation-select" getPopupContainer={getPopupAnchor()}>
                                {vendorRecommendations.map((recommendation, index) => (
                                    <Option key={index} value={index} data-testid={`finway:vendor:form:recommendation-option:${recommendation}`}>
                                        {t(`input:vendor.recommendations.${recommendation.toLowerCase()}`)}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={16} md="16">
                        <Form.Item label={t("input:vendor.street_name")} name="streetName" rules={rules.streetName}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.street_name")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={8} md="8">
                        <Form.Item label={t("input:vendor.street_number")} name="streetNumber" rules={rules.streetNumber}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.street_number")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={12} md="12">
                        <Form.Item label={t("input:vendor.zip")} name="zip" rules={rules.zip}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.zip")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={12} md="12">
                        <Form.Item label={t("input:vendor.city")} name="city" rules={rules.city}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.city")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={12} md="12">
                        <Form.Item label={t("input:vendor.country")} name="country" rules={rules.country}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.country")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={12} md="12">
                        <Form.Item label={t("input:vendor.country_code")} name="countryCode" rules={rules.countryCode}>
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={doFilterSelect}
                                getPopupContainer={getPopupAnchor()}
                                placeholder={t("placeholder:vendor.country_code")}
                            >
                                {countryCodeOptions.map((option) => (
                                    <Option key={option.code} value={option.code} label={option.label}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("input:vendor.contact_person_name")} name="contactPersonName" rules={rules.contactPersonName}>
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.contact_person_name")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={12} md="12">
                        <Form.Item label={t("input:vendor.contact_person_email")} name="contactPersonEmail" rules={rules.contactPersonEmail}>
                            <Input type="email" placeholder={t("placeholder:vendor.contact_person_email")} autoComplete="off" />
                        </Form.Item>
                    </Col>
                    <Col span={12} md="12">
                        <Form.Item
                            label={t("input:vendor.contact_person_phone")}
                            name="contactPersonPhone"
                            rules={rules.contactPersonPhone}
                            getValueFromEvent={handlePhoneNumberChange}
                        >
                            <Input
                                className="hidden-input-phone-number-no-border"
                                addonAfter={
                                    <PhoneInput
                                        key="contactPersonPhone"
                                        placeholder={phoneInputPlaceholder}
                                        onCountryChange={handleCountryChange}
                                        onChange={handlePhoneNumberChange}
                                        value={contactPersonPhone}
                                        className="bg-white"
                                    />
                                }
                            />
                        </Form.Item>
                    </Col>
                    <Col span={14} md="14">
                        <Form.Item label={t("input:vendor.tax_number")} rules={rules.taxNumber} name="taxNumber">
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.tax_number")} />
                        </Form.Item>
                    </Col>
                    <Col span={10} md="10">
                        <Form.Item label={t("input:vendor.vat_number")} rules={rules.vatNumber} name="vatNumber">
                            <Input maxLength={MAX_INPUT_LENGTH} placeholder={t("placeholder:vendor.vat_number")} />
                        </Form.Item>
                    </Col>
                    {(AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR_PAYMENT_DATA__ALL__CREATE) || loggedInUser.id === vendor?.createdBy || isNew) && (
                        <>
                            <Col span={14} md="14">
                                <Form.Item label={t("input:vendor.iban")} name="iban" rules={rules.iban}>
                                    <IbanInput disabled={!canEditVendor} />
                                </Form.Item>
                            </Col>
                            <Col span={10} md="10">
                                <Form.Item label={t("input:vendor.bic")} name="bic" rules={rules.bic}>
                                    <Input placeholder={t("placeholder:vendor.bic")} disabled={!canEditVendor} />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                    <Col span={fileList.length > 0 ? 24 : 20} md="20">
                        <Form.Item label={t("input:vendor.company_logo")} name="img" shouldUpdate>
                            <Upload
                                accept="image/*"
                                action={`${PROCESS_ENV.API_URL}/files`}
                                headers={{ Authorization: `Bearer ${localStorage.accessToken}` }}
                                listType="picture"
                                fileList={fileList}
                                onRemove={() => setFileList([])}
                                isImageUrl={(file: any) => !!file.type?.includes("image")}
                                // to avoid opening the URL on click
                                onPreview={() => {}}
                                customRequest={doFileUpload}
                                onChange={(data) => setFileList(data.fileList)}
                                progress={{
                                    strokeWidth: 3,
                                    showInfo: false,
                                    strokeColor: COLOR.secondary,
                                    className: "pr-20",
                                }}
                            >
                                {fileList.length === 0 && (
                                    <Button className="text-left btn-default">
                                        <UploadIcon />
                                        <span> {t("action:upload_company_logo")}</span>
                                    </Button>
                                )}
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </div>
    )
}
